/**
 * Turns a revision's before/after documents into the per-field list the console
 * renders (ENR-1363).
 *
 * The stored images are whole documents, so showing them raw would make the
 * reader diff two JSON blobs by eye to answer "what changed". The log exists to
 * answer that directly.
 *
 * A field is keyed `facet.field` because that pair is what the facet registry
 * names, and it is what an overlay will later address when it overrides or
 * suppresses one (PRD section 3.2).
 */

import type { ProfileFields } from "./types";

export type FieldChange = "added" | "changed" | "removed";

export interface FieldDiffEntry {
  path: string;
  change: FieldChange;
  before: unknown;
  after: unknown;
}

function flatten(fields: ProfileFields | null): Map<string, unknown> {
  const flat = new Map<string, unknown>();
  if (!fields) {
    return flat;
  }
  for (const [facetId, facet] of Object.entries(fields)) {
    for (const [fieldName, value] of Object.entries(facet)) {
      flat.set(`${facetId}.${fieldName}`, value);
    }
  }
  return flat;
}

/**
 * Structural equality over the value types a facet can declare: string, number,
 * boolean, and list of strings. Reference equality would report every list as
 * changed, because the two images are separately parsed JSON. A scoped
 * revision's field is a state object, compared by its state and value.
 */
function sameValue(a: unknown, b: unknown): boolean {
  if (isFieldState(a) && isFieldState(b)) {
    if (a.state === "SUPPRESS" || b.state === "SUPPRESS") return a.state === b.state;
    return sameValue(a.value, b.value);
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, index) => item === b[index]);
  }
  return a === b;
}

export function diffProfileFields(
  before: ProfileFields | null,
  after: ProfileFields | null
): FieldDiffEntry[] {
  const beforeFlat = flatten(before);
  const afterFlat = flatten(after);
  const paths = [...new Set([...beforeFlat.keys(), ...afterFlat.keys()])].sort();

  const entries: FieldDiffEntry[] = [];
  for (const path of paths) {
    const hadBefore = beforeFlat.has(path);
    const hasAfter = afterFlat.has(path);
    const beforeValue = beforeFlat.get(path);
    const afterValue = afterFlat.get(path);

    if (hadBefore && hasAfter) {
      if (!sameValue(beforeValue, afterValue)) {
        entries.push({ path, change: "changed", before: beforeValue, after: afterValue });
      }
      continue;
    }
    if (hasAfter) {
      entries.push({ path, change: "added", before: undefined, after: afterValue });
      continue;
    }
    entries.push({ path, change: "removed", before: beforeValue, after: undefined });
  }
  return entries;
}

/**
 * An empty string is rendered as `""` rather than as nothing: clearing a field
 * and writing an empty string are different acts, and the log has to keep them
 * distinguishable. Phase 1 makes this load-bearing, where an empty-string
 * override and a suppression must never look alike (PRD section 3.2).
 */
export function formatFieldValue(value: unknown): string {
  if (value === undefined || value === null) {
    return "-";
  }
  // A Space or org-unit revision records an overlay's field state, not a value.
  if (isFieldState(value)) {
    return value.state === "SUPPRESS" ? "Hidden in this scope" : formatFieldValue(value.value);
  }
  if (Array.isArray(value)) {
    return value.map((item) => String(item)).join(", ");
  }
  if (value === "") {
    return '""';
  }
  return String(value);
}

function isFieldState(
  value: unknown
): value is { state: "OVERRIDE"; value: unknown } | { state: "SUPPRESS" } {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const state = (value as Record<string, unknown>)["state"];
  return state === "SUPPRESS" || (state === "OVERRIDE" && "value" in value);
}
